import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, Minimize2, X, Truck, Tag, Users, Activity, AlertTriangle, Package } from "lucide-react";
import WorldShipmentMap from "components/admin/WorldShipmentMap";
import ConversionFunnel from "components/admin/ConversionFunnel";
import GiantStat from "components/admin/GiantStat";
import AdminCard from "components/admin/AdminCard";
import StatusBadge from "components/admin/StatusBadge";
import { Button } from "components/ui/button";
import CustomAreaChart from "components/CustomAreaChart";
import CustomBarChart from "components/CustomBarChart";
import { adminPaths } from "app/paths";
import {
  AdminDashboardSummary,
  AdminDashboardPeriodSummary,
  getAdminDashboardSummary,
  getAdminDashboardPeriodSummary,
  getEnviosPorDia,
  getEnviosPorPais,
  getFunilConversao,
  getSlaTransportadora,
  getEventosRecentes,
  DashboardEvento,
  PaisEnvios,
} from "services/admin/adminDashboard.service";
import {
  getIntegracoesStatus,
  AdminIntegrationStatus,
  IntegrationState,
} from "services/admin/adminIntegracoes.service";
import { ChartPoint } from "services/admin/adminCommon.types";

const SLIDE_DURATION_MS = 9000;
const REFRESH_MS = 60000;
const EVENTS_POLL_MS = 12000;

const EVENT_STYLE: Record<DashboardEvento["type"], { icon: typeof Package; border: string; iconColor: string }> = {
  pedido_importado: { icon: Package, border: "border-l-brand-primary", iconColor: "text-brand-primary" },
  etiqueta_emitida: { icon: Tag, border: "border-l-green-500", iconColor: "text-green-500" },
  sync_error: { icon: AlertTriangle, border: "border-l-red-500", iconColor: "text-red-500" },
};

const last7DaysRange = () => {
  const today = new Date();
  const date_to = today.toISOString().slice(0, 10);
  const from = new Date(today);
  from.setDate(from.getDate() - 6);
  return { date_from: from.toISOString().slice(0, 10), date_to };
};

const STATE_TONE: Record<IntegrationState, { label: string; tone: "green" | "yellow" | "red" }> = {
  operational: { label: "Operacional", tone: "green" },
  maintenance: { label: "Manutenção", tone: "yellow" },
  instability: { label: "Instabilidade", tone: "red" },
};

const SLIDE_TITLES = [
  "Visão geral",
  "Envios por dia",
  "Envios por país",
  "Funil de conversão",
  "SLA por transportadora",
  "Status das integrações",
];

const AdminSalaOperacoes = () => {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  const [slide, setSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [summary, setSummary] = useState<AdminDashboardSummary | null>(null);
  const [periodSummary, setPeriodSummary] = useState<AdminDashboardPeriodSummary | null>(null);
  const [enviosPorDia, setEnviosPorDia] = useState<ChartPoint[]>([]);
  const [enviosPorPais, setEnviosPorPais] = useState<PaisEnvios[]>([]);
  const [funil, setFunil] = useState<ChartPoint[]>([]);
  const [sla, setSla] = useState<Array<{ label: string; value: number; total: number }>>([]);
  const [integracoes, setIntegracoes] = useState<AdminIntegrationStatus[]>([]);
  const [feed, setFeed] = useState<DashboardEvento[]>([]);
  const sinceRef = useRef<string | undefined>(undefined);

  const loadData = useCallback(() => {
    const range = last7DaysRange();
    getAdminDashboardSummary().then(setSummary).catch(() => {});
    getAdminDashboardPeriodSummary(range).then(setPeriodSummary).catch(() => {});
    getEnviosPorDia(range).then(setEnviosPorDia).catch(() => {});
    getEnviosPorPais(range).then(setEnviosPorPais).catch(() => {});
    getFunilConversao(range).then(setFunil).catch(() => {});
    getSlaTransportadora(range).then(setSla).catch(() => {});
    getIntegracoesStatus().then(setIntegracoes).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
    const dataTimer = setInterval(loadData, REFRESH_MS);
    return () => clearInterval(dataTimer);
  }, [loadData]);

  const pollEvents = useCallback(() => {
    getEventosRecentes(sinceRef.current)
      .then(({ eventos, serverTime }) => {
        sinceRef.current = serverTime;
        if (eventos.length) {
          setFeed((prev) => [...eventos, ...prev].slice(0, 6));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    pollEvents();
    const eventsTimer = setInterval(pollEvents, EVENTS_POLL_MS);
    return () => clearInterval(eventsTimer);
  }, [pollEvents]);

  useEffect(() => {
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockTimer);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSlide((s) => (s + 1) % SLIDE_TITLES.length);
    }, SLIDE_DURATION_MS);
    return () => clearTimeout(t);
  }, [slide]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const exit = () => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    navigate(adminPaths.dashboard);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-brand-bg font-Inter text-brand-text">
      <style>{`
        @keyframes feedSlideIn {
          from { opacity: 0; transform: translateX(16px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .feed-item { animation: feedSlideIn 0.3s ease-out; }
      `}</style>

      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-8 py-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">
            Intrex · Sala de Operações
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums text-slate-900">
            {now.toLocaleTimeString("pt-BR")}
            <span className="ml-3 text-base font-medium text-slate-400">
              {now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
          <Button variant="outline" size="icon" onClick={exit}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-8 py-6">
        <div className="w-full max-w-6xl">
          {slide === 0 && (
            <AdminCard
              title="Visão geral"
              action={<span className="text-xs text-slate-400">Últimos 7 dias</span>}
            >
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <GiantStat label="Envios hoje" value={summary?.enviosHoje ?? "—"} icon={<Truck />} />
                <GiantStat
                  label="Envios (7 dias)"
                  value={periodSummary?.enviosNoPeriodo ?? "—"}
                  icon={<Truck />}
                />
                <GiantStat
                  label="Etiquetas emitidas (7d)"
                  value={periodSummary?.etiquetasEmitidas ?? "—"}
                  icon={<Tag />}
                />
                <GiantStat label="Clientes ativos" value={summary?.clientesAtivos ?? "—"} icon={<Users />} />
                <GiantStat
                  label="APIs online"
                  value={`${summary?.apisOnline ?? 0}/${summary?.totalIntegracoes ?? 0}`}
                  icon={<Activity />}
                  tone={
                    summary && summary.apisOnline < summary.totalIntegracoes ? "danger" : "success"
                  }
                />
                <GiantStat
                  label="Integrações com erro"
                  value={summary?.integracoesComErro ?? 0}
                  icon={<AlertTriangle />}
                  tone={summary && summary.integracoesComErro > 0 ? "danger" : "default"}
                />
              </div>
            </AdminCard>
          )}

          {slide === 1 && (
            <AdminCard title="Envios por dia" action={<span className="text-xs text-slate-400">Últimos 7 dias</span>}>
              <CustomAreaChart data={enviosPorDia} className="h-[420px]" />
            </AdminCard>
          )}

          {slide === 2 && (
            <AdminCard title="Envios por país" action={<span className="text-xs text-slate-400">Últimos 7 dias</span>}>
              <WorldShipmentMap data={enviosPorPais} />
            </AdminCard>
          )}

          {slide === 3 && (
            <AdminCard title="Funil de conversão" action={<span className="text-xs text-slate-400">Últimos 7 dias</span>}>
              <ConversionFunnel data={funil} />
            </AdminCard>
          )}

          {slide === 4 && (
            <AdminCard
              title="SLA por transportadora"
              action={<span className="text-xs text-slate-400">Últimos 7 dias</span>}
            >
              {sla.length ? (
                <CustomBarChart
                  data={sla}
                  xKey="label"
                  series={[{ key: "value", label: "Dias médios", color: "#2563EB" }]}
                  height={400}
                />
              ) : (
                <p className="py-20 text-center text-sm text-muted-foreground">
                  Nenhuma entrega concluída no período.
                </p>
              )}
            </AdminCard>
          )}

          {slide === 5 && (
            <AdminCard title="Status das integrações">
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {integracoes.map((s) => {
                  const tone = STATE_TONE[s.state];
                  return (
                    <div
                      key={s.key}
                      className="flex flex-col gap-2 rounded-xl border border-slate-200/80 px-5 py-4"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-800">{s.label}</span>
                        <StatusBadge label={tone.label} tone={tone.tone} />
                      </div>
                      {s.message && <span className="text-xs text-slate-400">{s.message}</span>}
                    </div>
                  );
                })}
              </div>
            </AdminCard>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-2 border-t border-slate-200 bg-white py-4">
        {SLIDE_TITLES.map((title, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            title={title}
            className={`h-1.5 rounded-full transition-all ${
              i === slide ? "w-8 bg-brand-primary" : "w-1.5 bg-slate-200 hover:bg-slate-300"
            }`}
          />
        ))}
      </div>

      <div className="pointer-events-none fixed bottom-6 right-6 z-10 flex w-[340px] flex-col-reverse gap-2">
        {feed.map((ev) => {
          const style = EVENT_STYLE[ev.type];
          const Icon = style.icon;
          return (
            <div
              key={ev.id}
              className={`feed-item flex items-start gap-3 rounded-lg border border-slate-200 border-l-4 bg-white px-4 py-3 shadow-lg ${style.border}`}
            >
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconColor}`} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{ev.titulo}</p>
                <p className="truncate text-xs text-slate-500">{ev.detalhe}</p>
                {ev.valorBrl != null && (
                  <p className="mt-0.5 text-xs font-medium text-green-600">
                    {ev.valorBrl.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminSalaOperacoes;
